<template>
	<div id="menuPage">
		<!-- Logo -->
		<div class="logo-area">
			<div class="logo-icon">
				<img src="../assets/logo3.png" alt="万木智会">
			</div>
		</div>

		<!-- 菜单图标 -->
		<nav class="nav-items">
			<div v-for="item in visibleMenus" :key="item.key"
				:class="['nav-item', { active: isActive(item.key) }]"
				@click="handleSelect(item.key)">
				<i :class="item.icon"></i>
				<span class="tooltip">{{ item.label }}</span>
			</div>
		</nav>

		<!-- 底部用户 -->
		<div class="bottom-area">
			<el-dropdown @command="handleCommand" trigger="hover" placement="right-start">
				<div class="avatar-wrapper">
					<div class="avatar">
						{{ avatarLetter }}
					</div>
				</div>
				<el-dropdown-menu slot="dropdown">
					<el-dropdown-item disabled>
						<span style="font-weight:600;color:#1a1a2e">{{ userName }}</span>
						<el-tag size="mini" style="margin-left:8px" type="info">{{ roleLabel }}</el-tag>
					</el-dropdown-item>
					<el-dropdown-item divided command="changePwd">
						<i class="el-icon-key"></i> 修改密码
					</el-dropdown-item>
					<el-dropdown-item command="logOut">
						<i class="el-icon-switch-button"></i> 退出登录
					</el-dropdown-item>
				</el-dropdown-menu>
			</el-dropdown>
		</div>

		<!-- 修改密码弹窗 (首次登录强制 / 个人中心主动触发 共用) -->
		<el-dialog :title="forceChangePwd ? '首次登录请修改密码' : '修改密码'"
			:visible.sync="pwdDialogVisible" width="440px"
			:close-on-click-modal="false"
			:close-on-press-escape="!forceChangePwd"
			:show-close="!forceChangePwd"
			custom-class="compact-form-dialog"
			append-to-body>
			<el-alert v-if="forceChangePwd" type="warning" :closable="false" show-icon
				title="您的账号还在使用默认密码 (手机后6位), 请先修改后再使用系统"
				style="margin-bottom:14px" />
			<el-form ref="pwdForm" :model="pwdForm" :rules="pwdRules"
				label-width="90px" label-position="right" size="small">
				<el-form-item label="原密码" prop="oldPassword">
					<el-input v-model="pwdForm.oldPassword" show-password
						placeholder="新建账号默认为手机后6位" />
				</el-form-item>
				<el-form-item label="新密码" prop="newPassword">
					<el-input v-model="pwdForm.newPassword" show-password placeholder="至少 6 位" />
				</el-form-item>
				<el-form-item label="确认新密码" prop="confirmPassword">
					<el-input v-model="pwdForm.confirmPassword" show-password placeholder="再输入一次新密码" />
				</el-form-item>
			</el-form>
			<span slot="footer">
				<!-- 强制改密码模式: 不允许取消, 但提供"退出登录"逃生口, 防止另一端已改密码导致本端卡死 -->
				<el-button v-if="forceChangePwd" type="text" style="color:#909399;float:left"
					@click="logoutFromDialog">退出登录</el-button>
				<el-button v-if="!forceChangePwd" @click="pwdDialogVisible = false">取消</el-button>
				<el-button type="primary" :loading="pwdSubmitting" @click="submitChangePwd">提 交</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import { MENU_CONFIG, isOfficialRole } from '@/config/menu.js';

const ROLE_LABELS = {
	ADMIN: '管理员',
	OFFICIAL: '官员',
	OFFICIAL_PROJECT: '项目官员',
	OFFICIAL_SETTLEMENT: '结算官员',
	OFFICIAL_AUDIT: '审核官员',
	ASSISTANT: '助教',
	DOCTOR: '医生',
	SPONSOR: '企业',
};

export default {
	name: 'menuPage',
	data() {
		const validateConfirm = (rule, value, cb) => {
			if (value !== this.pwdForm.newPassword) cb(new Error('两次密码不一致'));
			else cb();
		};
		return {
			token: '',
			active: '',
			// 修改密码弹窗
			pwdDialogVisible: false,
			forceChangePwd: false,
			pwdSubmitting: false,
			pwdForm: { oldPassword: '', newPassword: '', confirmPassword: '' },
			pwdRules: {
				oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
				newPassword: [
					{ required: true, message: '请输入新密码', trigger: 'blur' },
					{ min: 6, message: '至少 6 位', trigger: 'blur' }
				],
				confirmPassword: [
					{ required: true, message: '请再次输入新密码', trigger: 'blur' },
					{ validator: validateConfirm, trigger: 'blur' }
				]
			}
		}
	},
	computed: {
		visibleMenus() {
			const role = this.token?.type;
			if (!role) return [];

			// 优先级 1: 后端下发的菜单列表 (sys_role_menu 配置, 是权威来源)
			// 管理员在"菜单配置"里勾掉的菜单, 此处必须立即消失, 不再做静态兜底
			const dynamicMenus = this.token?.menus;
			if (Array.isArray(dynamicMenus)) {
				return MENU_CONFIG.filter(m => dynamicMenus.includes(m.key));
			}

			// 优先级 2 (兜底): 后端旧版没下发菜单字段时, 用前端静态配置
			return MENU_CONFIG.filter(m => {
				if (m.roles.includes(role)) return true;
				if (role === 'OFFICIAL') {
					return m.roles.some(r => r.startsWith('OFFICIAL'));
				}
				return false;
			});
		},
		roleLabel() {
			return ROLE_LABELS[this.token?.type] || '';
		},
		userName() {
			return this.token?.fullName || this.token?.username || '用户';
		},
		avatarLetter() {
			const name = this.userName;
			return name ? name.charAt(name.length - 1) : 'U';
		}
	},
	created() {
		let token = JSON.parse(localStorage.getItem('token'));
		if (token) {
			this.token = token;
			// 首次登录(管理员新建账号)→ 弹窗强制修改密码
			if (token.mustChangePassword) {
				this.forceChangePwd = true;
				this.pwdDialogVisible = true;
			}
		} else {
			this.$router.push({ name: 'login' });
		}
		this.active = this.$route.name;
	},
	methods: {
		isActive(key) {
			const name = this.$route.name;
			if (name === key) return true;
			// 从"我的会议"进入的会议详情, 侧边栏继续高亮"我的会议"
			if (name === 'meetdetails' && this.$route.query?.from === 'myMeetings') {
				return key === 'myMeetings';
			}
			const childMap = {
				'projects': ['project', 'projectDetail', 'singleMeets', 'meetinglist', 'meetdetails', 'createmeet',
					'confmeet', 'coverupload', 'liveUrl', 'uploadFile', 'option',
					'projectlist', 'offocoalProject', 'officialSingleMeets',
					'officialMeetinglist', 'officialCreatemeet',
					'doctor', 'doctorinfo', 'reviewdoctor', 'creatdoc', 'perInfo', 'upInfo', 'selectPro'],
				'settlementCenter': ['settlementAudit', 'settleaccount', 'fileUpdataReviewWithOffcial',
					'fileListPreviewWithOfficial', 'settlementAuditNew'],
				'supplierCenterList': ['supplierSettlement', 'fileUpdataReview', 'fileListPreview',
					'fileListPreviewnew', 'viewDetail', 'settlementEdit', 'laborSettlement'],
				'myMeetings': ['meetingRoom', 'dmeetdetail', 'agreement', 'meetlist', 'meethistory',
					'officialMeetingDetail'],
				'aiRule': ['aiCheckItem', 'aiLlm', 'aiAuditTask'],
			};
			return childMap[key]?.includes(name) || false;
		},

		handleCommand(e) {
			if (e === 'logOut') {
				this.$confirm('确定退出登录？', '提示', {
					confirmButtonText: '确定',
					cancelButtonText: '取消',
					type: 'warning'
				}).then(() => {
					localStorage.clear();
					this.$router.push({ name: 'login' });
				});
			} else if (e === 'changePwd') {
				this.openChangePwd();
			}
		},

		/** 强制修改密码弹窗里的"退出登录"逃生口 (兜底: 另一端已改密码后, 本端卡死时使用) */
		logoutFromDialog() {
			this.pwdDialogVisible = false;
			localStorage.clear();
			this.$router.push({ name: 'login' });
		},

		/** 个人中心主动触发: 非强制 */
		openChangePwd() {
			this.forceChangePwd = false;
			this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' };
			this.pwdDialogVisible = true;
			this.$nextTick(() => { this.$refs.pwdForm && this.$refs.pwdForm.clearValidate(); });
		},

		async submitChangePwd() {
			const valid = await new Promise(resolve => this.$refs.pwdForm.validate(v => resolve(v)));
			if (!valid) return;
			this.pwdSubmitting = true;
			try {
				const service = (await import('@/request/request.js')).default;
				const res = await service.post(`/api/user/${this.token.id}/change-password`, {
					oldPassword: this.pwdForm.oldPassword,
					newPassword: this.pwdForm.newPassword
				});
				if (res && res.code === 200) {
					this.$message.success('密码修改成功, 请重新登录');
					localStorage.clear();
					this.pwdDialogVisible = false;
					setTimeout(() => this.$router.push({ name: 'login' }), 800);
				} else {
					this.$message.error((res && res.msg) || '修改失败');
				}
			} catch (err) {
				this.$message.error('修改失败: ' + (err.message || err));
			}
			this.pwdSubmitting = false;
		},

		handleSelect(key) {
			if (this.token.type === 'ASSISTANT') {
				localStorage.removeItem('regToken');
			}
			// 已在当前路由时不再跳转, 避免 NavigationDuplicated 报错
			if (this.$route.name === key) return;
			this.$router.push({ name: key }).catch(err => {
				// 兜底吞掉重复跳转和取消跳转两类异常
				if (err && err.name !== 'NavigationDuplicated' && !/redundant|cancelled/i.test(err.message)) {
					throw err;
				}
			});
		}
	}
}
</script>

<style lang="scss" scoped>
#menuPage {
	width: 72px;
	height: 100vh;
	background: #1a1a2e;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 16px 0;
	flex-shrink: 0;
	z-index: 10;

	.logo-area {
		margin-bottom: 24px;

		.logo-icon {
			width: 40px;
			height: 40px;
			border-radius: 12px;
			overflow: hidden;
			background: rgba(255,255,255,0.1);
			display: flex;
			align-items: center;
			justify-content: center;

			img {
				width: 28px;
				height: 28px;
				object-fit: contain;
				filter: brightness(2);
			}
		}
	}

	.nav-items {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 4px;
		width: 100%;
		padding: 0 12px;
	}

	.nav-item {
		position: relative;
		width: 48px;
		height: 48px;
		border-radius: 14px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
		color: rgba(255, 255, 255, 0.4);

		i {
			font-size: 20px;
			transition: all 0.25s;
		}

		.tooltip {
			position: absolute;
			left: 60px;
			background: #1a1a2e;
			color: #fff;
			padding: 6px 14px;
			border-radius: 8px;
			font-size: 13px;
			font-weight: 600;
			white-space: nowrap;
			opacity: 0;
			pointer-events: none;
			transform: translateX(-8px);
			transition: all 0.2s ease;
			box-shadow: 0 4px 16px rgba(0,0,0,0.3);
			z-index: 100;

			&::before {
				content: '';
				position: absolute;
				left: -4px;
				top: 50%;
				transform: translateY(-50%) rotate(45deg);
				width: 8px;
				height: 8px;
				background: #1a1a2e;
			}
		}

		&:hover {
			background: rgba(255, 255, 255, 0.08);
			color: rgba(255, 255, 255, 0.8);

			.tooltip {
				opacity: 1;
				transform: translateX(0);
			}
		}

		&.active {
			background: linear-gradient(135deg, #667eea, #764ba2);
			color: #fff;
			box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);

			i { transform: scale(1.1); }
		}
	}

	.bottom-area {
		margin-top: auto;
		padding-top: 16px;

		.avatar-wrapper {
			cursor: pointer;

			.avatar {
				width: 40px;
				height: 40px;
				border-radius: 12px;
				background: linear-gradient(135deg, #667eea, #764ba2);
				color: #fff;
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 15px;
				font-weight: 700;
				transition: all 0.25s;

				&:hover {
					transform: scale(1.08);
					box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
				}
			}
		}
	}
}
</style>
